import { toast } from "react-toastify";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import "./App.scss";
import { isRouteHidden } from "./b2bconfig";
import TwoFactorAuthentication from "./component/2fa";
import NotFound from "./component/NotFound";
import BonusList from "./component/bonus-list";
import Casino from "./component/casino";
import LaunchGame from "./component/casino/LaunchGame";
import Deposite from "./component/deposite";
import Home from "./component/home";
import Inplay from "./component/in-play";
import Login from "./component/login";
import MarketAnalysis from "./component/market-analysis";
import MultiMarket from "./component/multi-market";
import Profile from "./component/profile";
import AccountStatement from "./component/reports/AccountStatement";
import ProfitLoss from "./component/reports/ProfitLoss";
import UnsettledBets from "./component/reports/UnsettledBets";
import SignUp from "./component/signup";
import SportDetail from "./component/sport-detail";
import Sports from "./component/sports";
import Transactions from "./component/transactions";
import Withdraw from "./component/withdraw";
import AuthLayout from "./layout/AuthLayout";
import MainLayout from "./layout/MainLayout";
import RacingDetail from "./racing-detail";
import ReferAndEarn from "./reffer-earn";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { getPopupContentAsync } from "./store/slices/gameSlice";
import { fetchLogo } from "./store/slices/logoSlice";
import { getApiBaseUrl, getDomainWithoutSubdomain } from "./utils/domain";
import { getUserData, isAuthenticated } from "./utils/auth";
import CasinoLayout from "./layout/CasinoLayout";
import TransactionDetails from "./component/transaction-details";

function B2BRouteGuard() {
  const location = useLocation();

  // Block navigation to B2B-hidden routes at the router level.
  if (isRouteHidden(location?.pathname)) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

function RealUserOnlyRouteGuard() {
  const location = useLocation();
  const authUser = useAppSelector((state) => state.auth?.user);
  const authToken = useAppSelector((state) => state.auth?.token);
  const storedUser = typeof window !== "undefined" ? getUserData() : null;

  const loggedIn = Boolean(authToken) || isAuthenticated();
  const user = authUser || storedUser;
  const isDemoUser = Boolean(user?.is_demo) || Boolean(user?.isDemo);

  useEffect(() => {
    if (!loggedIn) {
      if (
        typeof window !== "undefined" &&
        typeof window.openLoginDialog === "function"
      ) {
        window.openLoginDialog();
      } else {
        toast.error("Please login to continue.", {
          toastId: "auth_required_toast",
        });
      }
    } else if (isDemoUser && location.pathname.includes("/casino")) {
      toast.error("Demo users are not allowed to play casino games.", {
        toastId: "demo_casino_restricted",
      });
    }
  }, [loggedIn, isDemoUser, location.pathname]);

  if (!loggedIn || (isDemoUser && location.pathname.includes("/casino"))) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

function App() {
  const dispatch = useAppDispatch();
  const logoUrl = useAppSelector((state) => state?.logo?.logoUrl);
  const authUser = useAppSelector((state) => state.auth?.user);
  const authToken = useAppSelector((state) => state.auth?.token);
  const [popupContentData, setPopupContentData] = useState(null);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [isPopupImageReady, setIsPopupImageReady] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  const hasInitializedAuthRef = useRef(false);
  const prevLoggedInRef = useRef(false);

  useLayoutEffect(() => {
    const baseUrl = getApiBaseUrl();

    const domainData = getDomainWithoutSubdomain(baseUrl);

    // Set dynamic metadata based on domain
    if (domainData) {
      document.title = domainData;
    }

    dispatch(fetchLogo());
  }, [dispatch]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => {
      setIsMobileDevice(window.innerWidth <= 768);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const popupImageUrl = useMemo(() => {
    if (!popupContentData) return "";
    if (isMobileDevice) {
      return (
        popupContentData.content_mobile ||
        popupContentData.content ||
        popupContentData.description ||
        ""
      );
    }
    return popupContentData.description || popupContentData.content || "";
  }, [popupContentData, isMobileDevice]);

  useEffect(() => {
    const isLoggedIn = Boolean(authToken || authUser);
    const popupShownStorageKey = "loginPopupShown";

    if (!hasInitializedAuthRef.current) {
      hasInitializedAuthRef.current = true;
      prevLoggedInRef.current = isLoggedIn;
      return;
    }

    const justLoggedIn = !prevLoggedInRef.current && isLoggedIn;
    const justLoggedOut = prevLoggedInRef.current && !isLoggedIn;
    prevLoggedInRef.current = isLoggedIn;

    if (typeof window !== "undefined" && justLoggedOut) {
      sessionStorage.removeItem(popupShownStorageKey);
    }

    if (!justLoggedIn) return;
    if (
      typeof window !== "undefined" &&
      sessionStorage.getItem(popupShownStorageKey) === "true"
    )
      return;

    const fetchPopupContent = async () => {
      const baseUrl = getApiBaseUrl();
      const host = getDomainWithoutSubdomain(baseUrl);
      if (!host) return;

      const action = await dispatch(getPopupContentAsync({ host }));
      if (!getPopupContentAsync.fulfilled.match(action)) return;

      const popupData = action.payload?.data || action.payload;
      if (!popupData?.is_active) return;

      const hasImage =
        popupData?.description ||
        popupData?.content_mobile ||
        popupData?.content;
      if (!hasImage) return;

      setPopupContentData(popupData);
      setShowLoginPopup(true);
      setIsPopupImageReady(false);
      if (typeof window !== "undefined") {
        sessionStorage.setItem(popupShownStorageKey, "true");
      }
    };

    fetchPopupContent();
  }, [authToken, authUser, dispatch]);

  const upsertHeadTag = useCallback((selector, create, updates) => {
    if (typeof document === "undefined") return;
    let el = document.querySelector(selector);
    if (!el) {
      el = create();
      document.head.appendChild(el);
    }
    updates(el);
  }, []);

  const updateBrandIcons = useCallback(
    (icon) => {
      if (!icon || typeof document === "undefined") return;

      upsertHeadTag(
        'link[rel="icon"]',
        () => {
          const link = document.createElement("link");
          link.setAttribute("rel", "icon");
          return link;
        },
        (el) => {
          el.setAttribute("href", icon);
        },
      );

      upsertHeadTag(
        'link[rel="shortcut icon"]',
        () => {
          const link = document.createElement("link");
          link.setAttribute("rel", "shortcut icon");
          return link;
        },
        (el) => {
          el.setAttribute("href", icon);
        },
      );

      upsertHeadTag(
        'link[rel="apple-touch-icon"]',
        () => {
          const link = document.createElement("link");
          link.setAttribute("rel", "apple-touch-icon");
          return link;
        },
        (el) => {
          el.setAttribute("href", icon);
        },
      );

      upsertHeadTag(
        'meta[property="og:image"]',
        () => {
          const meta = document.createElement("meta");
          meta.setAttribute("property", "og:image");
          return meta;
        },
        (el) => {
          el.setAttribute("content", icon);
        },
      );
    },
    [upsertHeadTag],
  );

  useLayoutEffect(() => {
    if (logoUrl) {
      updateBrandIcons(logoUrl);
    }
  }, [logoUrl, updateBrandIcons]);

  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route element={<B2BRouteGuard />}>
            <Route element={<AuthLayout />}>
              <Route path="/signup" element={<SignUp />} />
              <Route path="/login" element={<Login />} />
            </Route>

            <Route element={<MainLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/inplay" element={<Inplay />} />
              <Route path="/sports/:id" element={<Sports />} />
              <Route element={<RealUserOnlyRouteGuard />}>
                <Route path="/casino" element={<Casino />} />
              </Route>
              <Route path="/sports/detail/:id" element={<SportDetail />} />
              <Route path="/multi-market" element={<MultiMarket />} />
              <Route
                path="/sports/detail/:id/:subId"
                element={<RacingDetail />}
              />
              <Route path="/profile" element={<Profile />} />
              <Route path="/profile/stake-settings" element={<Profile />} />
              <Route path="/profile/change-password" element={<Profile />} />
              <Route
                path="/reports/account-statement"
                element={<AccountStatement />}
              />
              <Route
                path="/reports/unsettled-bets"
                element={<UnsettledBets />}
              />
              <Route path="/market-analysis" element={<MarketAnalysis />} />
              <Route element={<RealUserOnlyRouteGuard />}>
                <Route path="/withdraw" element={<Withdraw />} />
                <Route path="/deposit" element={<Deposite />} />
              </Route>
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/transaction-details/:id" element={<TransactionDetails />} />
              <Route
                path="/2fa-settings"
                element={<TwoFactorAuthentication />}
              />
              <Route path="/reports/profit-loss" element={<ProfitLoss />} />
              <Route path="/bonus-list" element={<BonusList />} />
              <Route path="/reffer-and-earn" element={<ReferAndEarn />} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route element={<CasinoLayout />}>
              <Route path="/dc/gamev1.1/:slug" element={<LaunchGame />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>

      {showLoginPopup && popupImageUrl && (
        <div className="login-popup-overlay" role="dialog" aria-modal="true">
          <div className="login-popup-card">
            {isPopupImageReady && (
              <button
                type="button"
                className="login-popup-close-btn"
                aria-label="Close popup"
                onClick={() => setShowLoginPopup(false)}
              >
                ×
              </button>
            )}
            <img
              src={popupImageUrl}
              alt="Login popup"
              onLoad={() => setIsPopupImageReady(true)}
              onError={() => setIsPopupImageReady(true)}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default App;
